// Report total-row alignment guard.
// Frappe DataTable renders the total row in a separate footer. In RTL
// reports the footer can keep a stale horizontal transform while the
// header/body are already aligned, which makes totals look shifted. Keep
// footer geometry tied to the visible table and isolate numeric bidi text.

const NUMERIC_RE = /[\d٠-٩]/;
const WORD_RE = /[A-Za-zء-ي]{3,}/;

function cellIndex(cell, fallback) {
	const dataIndex =
		cell.getAttribute("data-col-index") ||
		cell.getAttribute("data-column-index") ||
		cell.getAttribute("data-col-idx");
	if (dataIndex != null && dataIndex !== "") return String(dataIndex);

	const classMatch = String(cell.className || "").match(/\bdt-cell--col-(\d+)\b/);
	if (classMatch) return classMatch[1];

	return String(fallback);
}

function isNumericCell(cell) {
	const text = (cell.textContent || "").trim();
	if (!NUMERIC_RE.test(text)) return false;
	const stripped = text
		.replace(/[٠-٩\d.,٫٬+\-()£$€¥₹\s]/g, "")
		.replace(/\bor\b/gi, "")
		.trim();
	return !WORD_RE.test(stripped);
}

function styleValue(el, prop) {
	if (!el) return "";
	const inline = el.style[prop];
	if (inline) return inline;

	const computed = getComputedStyle(el)[prop];
	if (computed && computed !== "auto" && computed !== "none") return computed;

	return "";
}

function copyMetric(target, source, prop) {
	const value = styleValue(source, prop);
	if (value && target.style[prop] !== value) target.style[prop] = value;
}

function pxNumber(value) {
	const parsed = Number.parseFloat(String(value || "").replace("px", ""));
	return Number.isFinite(parsed) ? parsed : 0;
}

function elementWidth(el) {
	if (!el) return 0;
	return Math.max(
		pxNumber(el.style.width),
		pxNumber(getComputedStyle(el).width),
		el.getBoundingClientRect().width || 0,
		el.scrollWidth || 0,
		el.offsetWidth || 0,
		el.clientWidth || 0
	);
}

function cellsWidth(cells) {
	return cells.reduce((total, cell) => total + elementWidth(cell), 0);
}

function ancestorWidth(table) {
	const selectors = [
		".report-wrapper",
		".query-report",
		".layout-main-section",
		".layout-main-section-wrapper",
		".page-content",
		".page-wrapper",
	];

	return selectors.reduce((width, selector) => {
		const el = table.closest(selector);
		return Math.max(width, elementWidth(el));
	}, 0);
}

function uniqueTables() {
	return Array.from(
		document.querySelectorAll(".report-wrapper .datatable, .query-report .datatable, .report-view .datatable")
	).filter((table, index, tables) => tables.indexOf(table) === index);
}

function getHeaderRow(table) {
	return table.querySelector(".dt-header .dt-row-header") || table.querySelector(".dt-row-header");
}

function getSourceRow(table) {
	const rows = Array.from(table.querySelectorAll(".dt-scrollable .dt-row"));
	return (
		rows.find((row) => {
			if (!row.querySelector(".dt-cell")) return false;
			if (row.classList.contains("dt-row-filter")) return false;
			if (row.classList.contains("dt-row-header")) return false;
			if (row.classList.contains("dt-row-totalRow")) return false;
			return true;
		}) || getHeaderRow(table)
	);
}

function syncFooter(table, sourceRow, sourceCells, totalCells) {
	const footer = table.querySelector(".dt-footer");
	const header = table.querySelector(".dt-header");
	const scrollable = table.querySelector(".dt-scrollable");
	const totalRow = table.querySelector(".dt-row-totalRow");
	if (!footer || !totalRow) return;

	const fullWidth = Math.ceil(
		Math.max(
			elementWidth(sourceRow),
			elementWidth(header),
			elementWidth(scrollable),
			scrollable?.scrollWidth || 0,
			header?.scrollWidth || 0,
			footer.scrollWidth || 0,
			ancestorWidth(table),
			cellsWidth(sourceCells),
			cellsWidth(totalCells)
		)
	);

	if (fullWidth && totalRow.style.width !== `${fullWidth}px`) {
		totalRow.style.width = `${fullWidth}px`;
	}

	// DataTable normally mirrors body scroll to header/footer. When RTL
	// direction changes after render, the footer can keep the old offset.
	const headerTransform = header ? header.style.transform || getComputedStyle(header).transform : "";
	const transform = headerTransform && headerTransform !== "none" ? headerTransform : "translateX(0px)";
	if (footer.style.transform !== transform) {
		footer.style.transform = transform;
	}
}

function bindScrollSync(table) {
	if (table.dataset.e16TotalsScrollBound === "1") return;
	table.dataset.e16TotalsScrollBound = "1";

	const scrollable = table.querySelector(".dt-scrollable");
	if (scrollable) {
		scrollable.addEventListener("scroll", scheduleSync, { passive: true });
	}
}

function syncTable(table) {
	const headerCells = Array.from(table.querySelectorAll(".dt-row-header .dt-cell"));
	const sourceRow = getSourceRow(table);
	const sourceCells = sourceRow ? Array.from(sourceRow.querySelectorAll(".dt-cell")) : [];
	const totalCells = Array.from(table.querySelectorAll(".dt-row-totalRow .dt-cell"));
	if (!headerCells.length || !totalCells.length) return;

	const byIndex = new Map();
	headerCells.forEach((cell, i) => byIndex.set(cellIndex(cell, i), cell));
	sourceCells.forEach((cell, i) => byIndex.set(cellIndex(cell, i), cell));

	syncFooter(table, sourceRow, sourceCells, totalCells);
	bindScrollSync(table);

	totalCells.forEach((cell, i) => {
		const idx = cellIndex(cell, i);
		const source = byIndex.get(idx);
		cell.setAttribute("data-e16-total-col-index", idx);

		if (source) {
			copyMetric(cell, source, "left");
			copyMetric(cell, source, "right");
			copyMetric(cell, source, "width");
			copyMetric(cell, source, "minWidth");
			copyMetric(cell, source, "maxWidth");
			copyMetric(cell, source, "transform");

			const width = styleValue(source, "width");
			if (width) {
				cell.style.flex = `0 0 ${width}`;
			}
		}

		const numeric = isNumericCell(cell);
		cell.classList.toggle("e16-dt-numeric", numeric);
		const content = cell.querySelector(".dt-cell__content");
		if (content) {
			content.classList.toggle("e16-dt-numeric-content", numeric);
			if (numeric) {
				content.setAttribute("dir", "ltr");
			} else {
				content.removeAttribute("dir");
			}
		}
	});
}

let raf = 0;
function scheduleSync() {
	if (raf) return;
	raf = requestAnimationFrame(() => {
		raf = 0;
		uniqueTables().forEach(syncTable);
	});
}

let resizeTimer = 0;
function scheduleResizeSync() {
	clearTimeout(resizeTimer);
	resizeTimer = setTimeout(scheduleSync, 160);
}

function primeSync() {
	scheduleSync();
	[250, 700, 1400, 2600].forEach((delay) => setTimeout(scheduleSync, delay));
}

export const reportTotalsFix = {
	mount() {
		primeSync();

		document.addEventListener("e16:lang-applied", scheduleSync);

		try {
			if (window.frappe?.router) {
				frappe.router.on("change", () => setTimeout(primeSync, 300));
			}
		} catch (e) {}

		window.addEventListener("resize", scheduleResizeSync, { passive: true });
		document.addEventListener("mouseup", scheduleSync, true);
		document.addEventListener("pointerup", scheduleSync, true);
		document.addEventListener("touchend", scheduleSync, true);
	},
};
